<template>
  <div class="home-header-panel">
    <div class="header-main-info">
      <div class="title-with-badge">
        <h1 class="header-title">Trung Tâm Điều Hành & Phân Tích Dữ Liệu</h1>
        <div class="status-live-badge">
          <span class="pulse-dot"></span>
          <span class="live-text">{{ isRealtime ? 'Dữ liệu thời gian thực' : 'Đồng bộ GA4 & CSDL' }}</span>
        </div>
      </div>
      <p class="header-subtitle">
        Tổng quan hợp nhất giữa lưu lượng truy cập thực tế (Google Analytics 4) và hệ thống dữ liệu du lịch, dịch vụ & tin tức
      </p>
    </div>

    <div class="header-controls">
      <!-- 1. Time Range Selector -->
      <div class="control-group range-group">
        <span class="control-label">Thời gian:</span>
        <el-dropdown trigger="click" @command="handleSelectRange">
          <el-button class="range-selector-btn" size="small">
            <i :class="currentRangeOption.icon" class="range-icon"></i>
            <span class="range-text">{{ currentRangeLabel }}</span>
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown" class="home-range-dropdown-menu">
            <el-dropdown-item
              v-for="item in rangeOptions"
              :key="item.value"
              :command="item.value"
              :class="{ active: currentRange === item.value }"
            >
              <i :class="item.icon" class="item-icon"></i>
              <span>{{ item.label }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <!-- 2. Action Buttons (Export & Refresh) -->
      <div class="control-group export-buttons-group">
        <!-- Export Excel -->
        <el-button
          class="btn-export btn-excel"
          size="small"
          :loading="exportLoading"
          @click="$emit('export-excel')"
        >
          <i class="fas fa-file-excel btn-icon"></i>
          <span class="ml-2">Xuất Excel</span>
        </el-button>

        <!-- Export CSV -->
        <el-button
          class="btn-export btn-csv"
          size="small"
          :loading="exportLoading"
          @click="$emit('export-csv')"
        >
          <i class="fas fa-file-csv btn-icon"></i>
          <span class="ml-2">Xuất CSV</span>
        </el-button>

        <!-- Export PDF -->
        <el-button
          class="btn-export btn-pdf"
          size="small"
          :loading="exportLoading"
          @click="$emit('export-pdf')"
        >
          <i class="fas fa-file-pdf btn-icon"></i>
          <span class="ml-2">Xuất PDF</span>
        </el-button>

        <!-- Refresh Data -->
        <el-tooltip content="Tải lại dữ liệu mới nhất" placement="top">
          <el-button
            class="btn-refresh"
            size="small"
            icon="el-icon-refresh-right"
            circle
            @click="$emit('refresh')"
          ></el-button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
export const RANGE_OPTIONS = [
  { label: 'Realtime (30 phút)', value: 'realtime', icon: 'fas fa-bolt icon-orange' },
  { label: 'Hôm nay', value: 'today', icon: 'far fa-clock icon-blue' },
  { label: '7 ngày qua', value: '7days', icon: 'far fa-calendar-alt icon-amber' },
  { label: '30 ngày qua', value: '30days', icon: 'far fa-calendar-alt icon-amber' },
  { label: '90 ngày qua', value: '90days', icon: 'far fa-calendar-alt icon-amber' },
  { label: 'Tháng này', value: 'this_month', icon: 'far fa-calendar icon-purple' },
  { label: 'Năm nay', value: 'this_year', icon: 'far fa-calendar-check icon-purple' },
  { label: 'Tất cả', value: 'all', icon: 'fas fa-globe icon-pink' },
  { label: 'Tùy chỉnh...', value: 'custom', icon: 'far fa-calendar-plus icon-teal' }
]

export const RANGE_LABEL_MAP = {
  realtime: '30 phút qua',
  today: 'Hôm nay',
  '7days': '7 ngày qua',
  '30days': '30 ngày qua',
  '90days': '90 ngày qua',
  this_month: 'Tháng này',
  this_year: 'Năm nay',
  all: 'Toàn bộ thời gian'
}

export default {
  name: 'HomeHeader',
  props: {
    currentRange: {
      type: String,
      default: 'realtime'
    },
    customRangeLabel: {
      type: String,
      default: ''
    },
    exportLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rangeOptions: RANGE_OPTIONS
    }
  },
  computed: {
    isRealtime() {
      return this.currentRange === 'realtime'
    },
    currentRangeLabel() {
      if (this.currentRange === 'custom') {
        return this.customRangeLabel || 'Tùy chỉnh'
      }
      return RANGE_LABEL_MAP[this.currentRange] || '30 phút qua'
    },
    currentRangeOption() {
      return (
        this.rangeOptions.find((opt) => opt.value === this.currentRange) ||
        this.rangeOptions[0]
      )
    }
  },
  methods: {
    handleSelectRange(cmd) {
      this.$emit('select-range', cmd)
    }
  }
}
</script>

<style lang="scss" scoped>
.home-header-panel {
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  border: 1px solid #edf2f7;

  .header-main-info {
    .title-with-badge {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      .header-title {
        margin: 0;
        font-size: 21px;
        font-weight: 800;
        color: #1e293b;
        letter-spacing: -0.4px;
      }

      .status-live-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        padding: 4px 10px;
        border-radius: 20px;

        .pulse-dot {
          width: 8px;
          height: 8px;
          background-color: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          animation: pulse 1.6s infinite;
        }

        .live-text {
          font-size: 11.5px;
          font-weight: 700;
          color: #047857;
        }
      }
    }

    .header-subtitle {
      margin: 6px 0 0 0;
      font-size: 13.5px;
      color: #64748b;
      font-weight: 500;
    }
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;

    .control-group {
      display: flex;
      align-items: center;
      gap: 8px;

      .control-label {
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
      }
    }

    .range-selector-btn {
      height: 38px;
      padding: 0 16px;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      font-weight: 700;
      font-size: 13px;
      color: #1e293b;
      display: inline-flex;
      align-items: center;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;

      &:hover, &:focus {
        border-color: #8b5cf6;
        color: #8b5cf6;
      }

      .range-icon {
        margin-right: 8px;
        font-size: 14px;
      }

      .range-text {
        margin-right: 8px;
      }
    }

    .export-buttons-group {
      display: flex;
      align-items: center;
      gap: 8px;

      .btn-export {
        height: 38px;
        padding: 0 14px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

        .btn-icon {
          font-size: 14px;
        }

        &.btn-excel {
          background-color: #107c41;
          color: #ffffff;
          &:hover {
            background-color: #0b6032;
            box-shadow: 0 4px 8px rgba(16, 124, 65, 0.25);
          }
        }

        &.btn-csv {
          background-color: #0284c7;
          color: #ffffff;
          &:hover {
            background-color: #0369a1;
            box-shadow: 0 4px 8px rgba(2, 132, 199, 0.25);
          }
        }

        &.btn-pdf {
          background-color: #e11d48;
          color: #ffffff;
          &:hover {
            background-color: #be123c;
            box-shadow: 0 4px 8px rgba(225, 29, 72, 0.25);
          }
        }
      }

      .btn-refresh {
        height: 38px;
        width: 38px;
        border-radius: 8px;
        border: 1px solid #cbd5e1;
        color: #475569;
        font-size: 15px;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &:hover {
          color: #8b5cf6;
          border-color: #8b5cf6;
          background-color: #f5f3ff;
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.home-range-dropdown-menu {
  .el-dropdown-menu__item {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 18px;
    display: flex;
    align-items: center;
    gap: 10px;

    .item-icon {
      font-size: 13px;
      width: 16px;
    }

    &.active {
      color: #8b5cf6;
      background-color: #f5f3ff;
      font-weight: 700;
    }
  }
}

.icon-orange { color: #f97316; }
.icon-blue { color: #3b82f6; }
.icon-amber { color: #f59e0b; }
.icon-purple { color: #8b5cf6; }
.icon-pink { color: #ec4899; }
.icon-teal { color: #0d9488; }
</style>
